'use client'

import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { motion } from 'framer-motion'
import { ArrowDownIcon, ArrowUpRightIcon } from 'lucide-react'

// Nodes: [x%, y%, radius, isHub, pulseDelay, pulseDuration]
const NODES = [
  { x: 7, y: 18, r: 2, hub: false, delay: 0.3, dur: 3.2 },
  { x: 22, y: 7, r: 2, hub: false, delay: 0.8, dur: 2.9 },
  { x: 46, y: 11, r: 2.5, hub: false, delay: 1.2, dur: 3.6 },
  { x: 70, y: 7, r: 2, hub: false, delay: 0.5, dur: 2.7 },
  { x: 87, y: 18, r: 2.5, hub: false, delay: 0.1, dur: 3.9 },
  { x: 94, y: 40, r: 2, hub: false, delay: 1.5, dur: 3.0 },
  { x: 90, y: 64, r: 2, hub: false, delay: 0.7, dur: 3.4 },
  { x: 72, y: 82, r: 2.5, hub: false, delay: 1.0, dur: 3.7 },
  { x: 48, y: 88, r: 2, hub: false, delay: 0.4, dur: 2.8 },
  { x: 24, y: 82, r: 2, hub: false, delay: 1.3, dur: 3.2 },
  { x: 8, y: 64, r: 2.5, hub: false, delay: 0.9, dur: 3.5 },
  { x: 4, y: 40, r: 2, hub: false, delay: 0.6, dur: 2.6 },
  { x: 19, y: 28, r: 2, hub: false, delay: 1.4, dur: 3.1 },
  { x: 76, y: 24, r: 2, hub: false, delay: 0.2, dur: 3.3 },
  // Hub nodes — larger, glowing, with ping rings
  { x: 30, y: 42, r: 5, hub: true, delay: 0.2, dur: 4.0 },
  { x: 57, y: 38, r: 5, hub: true, delay: 0.7, dur: 4.6 },
  { x: 78, y: 52, r: 4, hub: true, delay: 1.1, dur: 4.2 },
  { x: 44, y: 68, r: 4, hub: true, delay: 0.4, dur: 3.8 },
]

// [fromIdx, toIdx, travelDuration, delayFraction]
const EDGES: Array<{ f: number; t: number; dur: number; d: number }> = [
  // Outer ring
  { f: 0, t: 1, dur: 3.8, d: 0.0 },
  { f: 1, t: 2, dur: 4.2, d: 0.3 },
  { f: 2, t: 3, dur: 3.5, d: 0.7 },
  { f: 3, t: 4, dur: 4.0, d: 0.1 },
  { f: 4, t: 5, dur: 3.6, d: 0.5 },
  { f: 5, t: 6, dur: 4.4, d: 0.9 },
  { f: 6, t: 7, dur: 3.9, d: 0.2 },
  { f: 7, t: 8, dur: 4.1, d: 0.6 },
  { f: 8, t: 9, dur: 3.7, d: 0.0 },
  { f: 9, t: 10, dur: 4.3, d: 0.4 },
  { f: 10, t: 11, dur: 3.5, d: 0.8 },
  { f: 11, t: 0, dur: 4.0, d: 0.3 },
  // Spokes to hubs
  { f: 11, t: 14, dur: 3.2, d: 0.1 },
  { f: 0, t: 14, dur: 3.6, d: 0.6 },
  { f: 12, t: 14, dur: 3.4, d: 0.0 },
  { f: 1, t: 14, dur: 4.0, d: 0.9 },
  { f: 2, t: 15, dur: 3.3, d: 0.4 },
  { f: 3, t: 15, dur: 3.7, d: 0.0 },
  { f: 13, t: 15, dur: 4.1, d: 0.7 },
  { f: 4, t: 16, dur: 3.5, d: 0.2 },
  { f: 5, t: 16, dur: 3.9, d: 0.5 },
  { f: 6, t: 16, dur: 4.2, d: 0.1 },
  { f: 9, t: 17, dur: 3.4, d: 0.8 },
  { f: 8, t: 17, dur: 4.0, d: 0.3 },
  { f: 7, t: 17, dur: 3.6, d: 0.6 },
  // Hub–hub backbone
  { f: 14, t: 15, dur: 2.8, d: 0.0 },
  { f: 15, t: 16, dur: 3.0, d: 0.5 },
  { f: 14, t: 17, dur: 3.2, d: 0.2 },
  { f: 15, t: 17, dur: 2.9, d: 0.7 },
  { f: 16, t: 17, dur: 3.1, d: 0.4 },
]

export function Hero() {
  return (
    <section className="relative min-h-[calc(100vh-64px)] flex items-center justify-center overflow-hidden bg-[#07090f]">
      {/* Grid background */}
      <div className="absolute inset-0 grid-overlay opacity-40" />

      {/* Nerve network SVG */}
      <svg
        className="absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{ pointerEvents: 'none' }}
      >
        <defs>
          <style>{`
            @keyframes nerve-signal {
              from { stroke-dashoffset: 0; }
              to   { stroke-dashoffset: -3200; }
            }
            @keyframes nerve-signal-rev {
              from { stroke-dashoffset: 0; }
              to   { stroke-dashoffset: 3200; }
            }
          `}</style>

          {/* Soft glow for hub nodes */}
          <filter id="hub-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Subtle glow for regular nodes */}
          <filter id="node-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ── Structural lines (static, very faint) ── */}
        {EDGES.map((e, i) => (
          <line
            key={`sl-${i}`}
            x1={`${NODES[e.f].x}%`}
            y1={`${NODES[e.f].y}%`}
            x2={`${NODES[e.t].x}%`}
            y2={`${NODES[e.t].y}%`}
            stroke="#00c8ff"
            strokeOpacity="0.1"
            strokeWidth="0.75"
          />
        ))}

        {/* ── Traveling signal pulses ── */}
        {EDGES.map((e, i) => (
          <line
            key={`sig-${i}`}
            x1={`${NODES[e.f].x}%`}
            y1={`${NODES[e.f].y}%`}
            x2={`${NODES[e.t].x}%`}
            y2={`${NODES[e.t].y}%`}
            stroke="#00c8ff"
            strokeOpacity="0.85"
            strokeWidth={NODES[e.f].hub || NODES[e.t].hub ? 2 : 1.5}
            strokeDasharray="12 3200"
            strokeLinecap="round"
            style={{
              animation: `${i % 2 === 0 ? 'nerve-signal' : 'nerve-signal-rev'} ${e.dur}s linear infinite`,
              animationDelay: `${-(e.d * e.dur).toFixed(2)}s`,
            }}
          />
        ))}

        {/* ── Hub ping rings ── */}
        {NODES.filter((n) => n.hub).map((node, i) => (
          <motion.circle
            key={`ping-${i}`}
            cx={`${node.x}%`}
            cy={`${node.y}%`}
            r={node.r}
            fill="none"
            stroke="#00c8ff"
            strokeWidth="1"
            initial={{ opacity: 0.7 }}
            animate={{ r: [node.r, node.r * 7], opacity: [0.7, 0] }}
            transition={{
              duration: node.dur * 0.75,
              repeat: Infinity,
              ease: 'easeOut',
              delay: node.delay,
            }}
          />
        ))}

        {/* ── Second staggered ping rings on hubs ── */}
        {NODES.filter((n) => n.hub).map((node, i) => (
          <motion.circle
            key={`ping2-${i}`}
            cx={`${node.x}%`}
            cy={`${node.y}%`}
            r={node.r}
            fill="none"
            stroke="#7c3aed"
            strokeWidth="0.75"
            initial={{ opacity: 0 }}
            animate={{ r: [node.r, node.r * 5], opacity: [0.5, 0] }}
            transition={{
              duration: node.dur * 0.75,
              repeat: Infinity,
              ease: 'easeOut',
              delay: node.delay + node.dur * 0.375,
            }}
          />
        ))}

        {/* ── Regular nodes ── */}
        {NODES.filter((n) => !n.hub).map((node, i) => (
          <motion.circle
            key={`nd-${i}`}
            cx={`${node.x}%`}
            cy={`${node.y}%`}
            r={node.r}
            fill="#00c8ff"
            filter="url(#node-glow)"
            animate={{ opacity: [0.35, 0.85, 0.35] }}
            transition={{
              duration: node.dur,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: node.delay,
            }}
          />
        ))}

        {/* ── Hub nodes (bright, glowing) ── */}
        {NODES.filter((n) => n.hub).map((node, i) => (
          <motion.circle
            key={`hub-${i}`}
            cx={`${node.x}%`}
            cy={`${node.y}%`}
            r={node.r}
            fill="#00c8ff"
            filter="url(#hub-glow)"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{
              duration: node.dur,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: node.delay,
            }}
          />
        ))}
      </svg>

      {/* Radial gradient — darkens edges so network fades out, center stays clear for text */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 75% 65% at 50% 50%, transparent 20%, #07090f 80%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="space-y-8 max-w-4xl mx-auto"
        >
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#1c2333] bg-[#0d1117] text-[#00c8ff] text-xs font-mono tracking-widest uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00c8ff] animate-pulse" />
            Orchestration Platform
          </div>

          {/* Headline */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight text-[#e2e8f0] leading-none">
            The Nerve Center <br className="hidden md:block" />
            <span className="text-[#00c8ff]">for Physical Operations</span>
          </h1>

          {/* Subhead */}
          <p className="mx-auto max-w-2xl text-lg md:text-xl text-[#94a3b8] leading-relaxed">
            A user taps. A gate opens. A vehicle dispatches. A tenant gets their
            ride. We open doors in front of you — end-to-end automation across
            Mobility and Real Estate with no manual handoffs.
          </p>

          {/* Origin proof line */}
          <p className="text-sm text-[#94a3b8]/70 tracking-wide">
            Built by the team behind{' '}
            <span className="text-[#e2e8f0]/80 font-medium">Whim</span> — the
            world&apos;s first MaaS super-app.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link href="/contact">
              <Button
                size="lg"
                className="bg-[#00c8ff] text-[#07090f] font-semibold hover:bg-[#00b8ef] transition-colors glow-cyan"
              >
                Book a Demo <ArrowUpRightIcon className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="#proof">
              <Button
                size="lg"
                variant="outline"
                className="border-[#1c2333] text-[#e2e8f0] hover:bg-[#0d1117] transition-colors"
              >
                See Our Work
              </Button>
            </a>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          aria-hidden="true"
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#94a3b8]"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ArrowDownIcon className="h-5 w-5" />
        </motion.div>
      </div>
    </section>
  )
}
